import { rem } from 'polished'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import TextButton from 'LuxKit/Button/TextButton'
import { SVG_ICON_SIZES } from 'LuxKit/SvgIcon'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import getPropertyIcon from 'lib/offer/getPropertyIcon'

const Facilities = styled.ul`
  display: grid;
  gap: ${rem(8)} ${rem(16)};
  margin: 0;

  // Two columns on mobile, possibly more on tablet and desktop.
  grid-template-columns: repeat(2, 1fr);
  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(auto-fill, minmax(${rem(180)}, 1fr));
  }
`

const Facility = styled.li`
  display: flex;
  gap: ${rem(8)};
  margin: 0;

  // Align icon with first line of text (in case it wraps).
  align-items: start;
  line-height: ${SVG_ICON_SIZES.S};
`

interface Props {
  popularFacilities: Array<App.Property.AmenityItem>
}

function PropertyPopularFacilities({ popularFacilities }: Props) {
  // TODO(HP-1614): Consider supporting LAB-172 and boosting whatever matches active amenity filters
  // to the start of the list.
  //
  // NB: We expect accommodation service to cap the number of popular facilities to something
  // sensible, like six.
  const displayPopularFacilities = useMemo(() => popularFacilities.map(({ name, iconName }) => ({
    key: name,
    name,
    Icon: getPropertyIcon(iconName),
  })), [popularFacilities])

  return <Group direction="vertical" gap={8}>
    {/* TODO(HP-1614): Unify heading variants. Descriptions proxied from bedbank service tend to use
      * level 4 headings, but `<OfferPromotions/>` (which admittedly is pretty rare) uses a level 3
      * heading.
      */}
    <Heading variant="heading4" as="h4">Popular facilities</Heading>
    <Facilities>
      {displayPopularFacilities.map(({ key, name, Icon }) => <Facility key={key}>
        <Icon size="S" />{name}
      </Facility>)}
    </Facilities>
    {/* TODO(HP-1614): The CTA currently links to `#facilities`, which is the correct fragment for
      * `<NavigableOfferContent/>`s in `<OfferPage/>`, but not in `<BedbankOfferPage/>` (where it's
      * `#facilities-all`). Once we switch to our own `<NavigableOfferContent/>`, this won't be a
      * problem.
      *
      * NB: The `<div/>` wrapper is a hack to prevent the button from taking up the container's full
      * width.
      */}
    <div>
      <TextButton horizontalOutdent="start" kind="tertiary" href="#facilities">View all facilities</TextButton>
    </div>
  </Group>
}

export default React.memo(PropertyPopularFacilities)
